import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { usePrestadores, useCriarPrestador, useAtualizarPrestador } from '@/hooks/usePrestadores';
import { useProfiles, useAtendentes, useAdmins, useProfilesComRoles } from '@/hooks/useProfiles';
import { useDesativarMembro, useReativarMembro } from '@/hooks/useMembros';
import { podeDesativarMembro } from '@/lib/member-deactivation';
import { useAuthContext } from '@/contexts/AuthContext';
import { PrestadorServico } from '@/types/ordemServico';
import { useServicosAtivos } from '@/hooks/useServicos';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';
import { PlanLimitBanner } from '@/components/common/PlanLimitBanner';
import { InviteDialog } from '@/components/equipe/InviteDialog';
import { PendingInvitesList } from '@/components/equipe/PendingInvitesList';
import { usePlanLimits } from '@/hooks/useOrganization';
import {
  Plus,
  Loader2,
  Edit,
  Search,
  Eye,
  UserPlus,
  UserMinus,
  UserCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type Tab = 'prestadores' | 'atendentes' | 'admins';

interface PillTabsProps {
  value: Tab;
  options: { value: Tab; label: string; count?: number }[];
  onChange: (next: Tab) => void;
}

function PillTabs({ value, options, onChange }: PillTabsProps) {
  return (
    <div className="inline-flex p-1 rounded-full bg-muted/60 gap-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${
            value === o.value
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {o.label}
          {o.count !== undefined && (
            <span className="text-[10px] font-mono opacity-70">{o.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

interface NameCellProps {
  inicial: string;
  nome: string;
  avatarUrl?: string | null;
}
function NameCell({ inicial, nome, avatarUrl }: NameCellProps) {
  return (
    <div className="tbl-name">
      <span className="tbl-avt" aria-hidden>
        {avatarUrl ? <img src={avatarUrl} alt="" /> : inicial}
      </span>
      <span className="tbl-name__nm">{nome}</span>
    </div>
  );
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="tbl-card">
      <table className="tbl">
        <tbody>
          {[1, 2, 3, 4].map((i) => (
            <tr key={i}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}>
                  <Skeleton className="h-5 w-full max-w-[180px]" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Equipe() {
  useFeatureTracking('equipe');
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { isAdmin, user, organization } = useAuthContext();
  const { data: prestadores, isLoading: loadingPrestadores } = usePrestadores();
  const { data: profiles } = useProfiles();
  const { data: atendentes, isLoading: loadingAtendentes } = useAtendentes(true);
  const { data: admins, isLoading: loadingAdmins } = useAdmins(true);
  const { data: prestadorMembers } = useProfilesComRoles('prestador', { incluirInativos: true });
  const criarPrestador = useCriarPrestador();
  const atualizarPrestador = useAtualizarPrestador();
  const desativarMembro = useDesativarMembro();
  const reativarMembro = useReativarMembro();
  const { data: servicosCatalogo } = useServicosAtivos();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const prestadorLimit = usePlanLimits('prestadores');
  const admLimit = usePlanLimits('admins');
  const anyLimitReached =
    prestadorLimit.data?.permitido === false || admLimit.data?.permitido === false;

  const [tabAtiva, setTabAtiva] = useState<Tab>('prestadores');
  const [termoBusca, setTermoBusca] = useState('');
  const [dialogAberto, setDialogAberto] = useState(false);
  const [prestadorEditando, setPrestadorEditando] = useState<PrestadorServico | null>(null);

  const [profileId, setProfileId] = useState('');
  const [servicosOferecidos, setServicosOferecidos] = useState<string[]>([]);
  const [observacoes, setObservacoes] = useState('');

  const resetForm = () => {
    setProfileId('');
    setServicosOferecidos([]);
    setObservacoes('');
    setPrestadorEditando(null);
  };

  const abrirDialogEditar = (prestador: PrestadorServico) => {
    setPrestadorEditando(prestador);
    setProfileId(prestador.profile_id);
    setServicosOferecidos(prestador.servicos_oferecidos);
    setObservacoes(prestador.observacoes);
    setDialogAberto(true);
  };

  const toggleServico = (tipo: string) => {
    setServicosOferecidos((prev) =>
      prev.includes(tipo) ? prev.filter((s) => s !== tipo) : [...prev, tipo],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (prestadorEditando) {
      await atualizarPrestador.mutateAsync({
        id: prestadorEditando.id,
        dados: { servicos_oferecidos: servicosOferecidos, observacoes },
      });
    } else {
      await criarPrestador.mutateAsync({
        profile_id: profileId,
        servicos_oferecidos: servicosOferecidos,
        observacoes,
      });
    }

    setDialogAberto(false);
    resetForm();
  };

  const profilesDisponiveis = profiles?.filter(
    (p) => !prestadores?.some((pr) => pr.profile_id === p.id),
  );

  const filtrarPorBusca = (nome: string | null) => {
    if (!termoBusca) return true;
    return nome?.toLowerCase().includes(termoBusca.toLowerCase());
  };

  const statusPorProfile = new Map(
    (prestadorMembers ?? []).map((m) => [m.id, { userId: m.user_id, ativo: m.ativo }]),
  );
  const prestadoresComStatus = (prestadores ?? []).map((p) => {
    const s = statusPorProfile.get(p.profile_id);
    return { ...p, _userId: s?.userId ?? null, _ativo: s?.ativo ?? true };
  });

  const prestadoresFiltrados = prestadoresComStatus
    .filter((p) => mostrarInativos || p._ativo)
    .filter((p) => filtrarPorBusca(p.profile?.nome || null) || p.profile?.telefone?.includes(termoBusca));
  const atendentesFiltrados = (atendentes ?? [])
    .filter((a) => mostrarInativos || a.ativo)
    .filter((a) => filtrarPorBusca(a.nome));
  const adminsFiltrados = (admins ?? [])
    .filter((a) => mostrarInativos || a.ativo)
    .filter((a) => filtrarPorBusca(a.nome));

  const isPending = criarPrestador.isPending || atualizarPrestador.isPending;

  if (!isAdmin) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center h-[60vh]">
          <EmptyState
            titulo={t('settings.restrictedAccess')}
            descricao={t('settings.restrictedDescription')}
          />
        </div>
      </MainLayout>
    );
  }

  const tabs: { value: Tab; label: string; count?: number }[] = [
    {
      value: 'prestadores',
      label: t('team.tabs.providers'),
      count: prestadoresComStatus.filter((p) => p._ativo).length,
    },
    {
      value: 'atendentes',
      label: t('team.tabs.agents'),
      count: (atendentes ?? []).filter((a) => a.ativo).length,
    },
    {
      value: 'admins',
      label: t('team.tabs.admins'),
      count: (admins ?? []).filter((a) => a.ativo).length,
    },
  ];

  function AcaoMembro({
    alvoUserId,
    ativo,
  }: {
    role: 'prestador' | 'atendente' | 'admin';
    alvoUserId: string | null;
    ativo: boolean;
  }) {
    if (!alvoUserId) return null;
    if (!ativo) {
      return (
        <button
          type="button"
          className="painel__icon-btn"
          title={t('team.reactivate.action')}
          aria-label={t('team.reactivate.action')}
          onClick={() => reativarMembro.mutate({ userId: alvoUserId })}
        >
          <UserCheck size={14} />
        </button>
      );
    }
    const isAlvoOwner = alvoUserId === organization?.owner_id;
    const guard = podeDesativarMembro({ alvoUserId, callerUserId: user?.id ?? '', isAlvoOwner });
    if (!guard.permitido) return null;
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            type="button"
            className="painel__icon-btn"
            title={t('team.deactivate.action')}
            aria-label={t('team.deactivate.action')}
          >
            <UserMinus size={14} />
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('team.deactivate.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('team.deactivate.confirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => desativarMembro.mutate({ userId: alvoUserId })}>
              {t('team.deactivate.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  const inviteAction = (
    <Button onClick={() => setInviteOpen(true)} disabled={anyLimitReached}>
      <UserPlus className="h-4 w-4 mr-2" />
      {t('team.addMember')}
    </Button>
  );

  return (
    <MainLayout>
      <div className="p-6 flex flex-col gap-5">
        <PageHeader
          titulo={t('team.title')}
          acoes={
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <button
                        type="button"
                        className="ow-btn ow-btn--primary ow-btn--sm"
                        onClick={() => setInviteOpen(true)}
                        disabled={anyLimitReached}
                      >
                        <Plus size={14} />
                        {t('invite.inviteButton.label')}
                      </button>
                    </span>
                  </TooltipTrigger>
                  {anyLimitReached && (
                    <TooltipContent>{t('invite.inviteButton.disabledTooltip')}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <UserMenu />
            </>
          }
        />

        <PlanLimitBanner tipo="prestadores" label="prestadores" />
        <PendingInvitesList />

        <PillTabs value={tabAtiva} options={tabs} onChange={setTabAtiva} />

        <div className="dst-toolbar">
          <label className="dst-toolbar__search">
            <Search size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
            <input
              type="text"
              placeholder={t('common.search') + '...'}
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground ml-auto cursor-pointer">
            <Switch checked={mostrarInativos} onCheckedChange={setMostrarInativos} />
            {t('team.showInactive')}
          </label>
        </div>

        {/* Prestadores */}
        {tabAtiva === 'prestadores' &&
          (loadingPrestadores ? (
            <TableSkeleton cols={3} />
          ) : prestadoresFiltrados && prestadoresFiltrados.length > 0 ? (
            <div className="tbl-card">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t('common.name')}</th>
                    <th>{t('common.phone')}</th>
                    <th style={{ width: 130 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {prestadoresFiltrados.map((prestador) => (
                    <tr
                      key={prestador.id}
                      onClick={() => navigate(`/equipe/prestador/${prestador.id}`)}
                      style={{ opacity: prestador._ativo ? 1 : 0.5 }}
                    >
                      <td>
                        <NameCell
                          inicial={prestador.profile?.nome?.charAt(0) || '?'}
                          nome={prestador.profile?.nome || t('common.noName')}
                          avatarUrl={prestador.profile?.avatar_url}
                        />
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {prestador.profile?.telefone || '-'}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1 justify-end">
                          <button
                            type="button"
                            className="painel__icon-btn"
                            onClick={() => navigate(`/equipe/prestador/${prestador.id}`)}
                            aria-label={t('team.provider.view')}
                            title={t('team.provider.view')}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            type="button"
                            className="painel__icon-btn"
                            onClick={() => abrirDialogEditar(prestador)}
                            aria-label={t('common.edit')}
                            title={t('common.edit')}
                          >
                            <Edit size={14} />
                          </button>
                          <AcaoMembro
                            role="prestador"
                            alvoUserId={prestador._userId}
                            ativo={prestador._ativo}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              titulo={t('team.provider.noProviders')}
              descricao={
                termoBusca ? t('common.noResults') : t('team.provider.addFirstProvider')
              }
              acao={!termoBusca && inviteAction}
            />
          ))}

        {/* Atendentes */}
        {tabAtiva === 'atendentes' &&
          (loadingAtendentes ? (
            <TableSkeleton cols={2} />
          ) : atendentesFiltrados && atendentesFiltrados.length > 0 ? (
            <div className="tbl-card">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t('common.name')}</th>
                    <th>{t('common.since', 'Desde')}</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {atendentesFiltrados.map((atendente) => (
                    <tr
                      key={atendente.id}
                      style={{ cursor: 'default', opacity: atendente.ativo ? 1 : 0.5 }}
                    >
                      <td>
                        <NameCell
                          inicial={atendente.nome?.charAt(0) || '?'}
                          nome={atendente.nome || t('common.noName')}
                          avatarUrl={atendente.avatar_url}
                        />
                      </td>
                      <td className="text-muted-foreground" style={{ fontSize: 12 }}>
                        {format(new Date(atendente.criado_em), 'dd/MM/yyyy', { locale: ptBR })}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">
                          <AcaoMembro
                            role="atendente"
                            alvoUserId={atendente.user_id}
                            ativo={atendente.ativo}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              titulo={t('team.agent.noAgents')}
              descricao={termoBusca ? t('common.noResults') : t('team.addMember')}
              acao={!termoBusca && inviteAction}
            />
          ))}

        {/* Admins */}
        {tabAtiva === 'admins' &&
          (loadingAdmins ? (
            <TableSkeleton cols={2} />
          ) : adminsFiltrados && adminsFiltrados.length > 0 ? (
            <div className="tbl-card">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t('common.name')}</th>
                    <th>{t('common.since', 'Desde')}</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {adminsFiltrados.map((admin) => (
                    <tr
                      key={admin.id}
                      style={{ cursor: 'default', opacity: admin.ativo ? 1 : 0.5 }}
                    >
                      <td>
                        <NameCell
                          inicial={admin.nome?.charAt(0) || '?'}
                          nome={admin.nome || t('common.noName')}
                          avatarUrl={admin.avatar_url}
                        />
                      </td>
                      <td className="text-muted-foreground" style={{ fontSize: 12 }}>
                        {format(new Date(admin.criado_em), 'dd/MM/yyyy', { locale: ptBR })}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">
                          <AcaoMembro role="admin" alvoUserId={admin.user_id} ativo={admin.ativo} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              titulo={t('team.admin.noAdmins')}
              descricao={termoBusca ? t('common.noResults') : t('team.addMember')}
              acao={!termoBusca && inviteAction}
            />
          ))}
      </div>

      {/* Dialog Criar/Editar Prestador Existente */}
      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {prestadorEditando ? t('common.edit') : t('team.provider.addExisting')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!prestadorEditando && (
              <div className="space-y-2">
                <Label>{t('common.user')}</Label>
                <Select value={profileId} onValueChange={setProfileId} required>
                  <SelectTrigger>
                    <SelectValue placeholder={t('team.provider.selectProfile')} />
                  </SelectTrigger>
                  <SelectContent>
                    {profilesDisponiveis?.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('team.provider.addExistingDescription')}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('team.provider.offeredServices')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {(servicosCatalogo ?? []).map((servico) => (
                  <div key={servico.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`servico-${servico.id}`}
                      checked={servicosOferecidos.includes(servico.nome)}
                      onCheckedChange={() => toggleServico(servico.nome)}
                    />
                    <label htmlFor={`servico-${servico.id}`} className="text-sm cursor-pointer">
                      {servico.nome}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('common.notes')}</Label>
              <Textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder={t('team.provider.notesPlaceholder')}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogAberto(false)}
                disabled={isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isPending || (!prestadorEditando && !profileId)}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {prestadorEditando ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </MainLayout>
  );
}
